import type { BankItem } from "../../engine/bank-types";

/**
 * 現實應用 — the 致用 gate.
 *
 * Every item puts the student in a situation they will actually meet, then
 * asks which course of action follows from the passage. Because they are
 * tagged by concept, any passage carrying that concept can draw on them —
 * which is how the gate reaches all 512 passages without a scenario having
 * to be written for each one.
 *
 * The distractors are the three realistic failure modes: applying the
 * passage too literally, applying its opposite while quoting it, and
 * applying a neighbouring principle that does not fit this case.
 */
export const APPLICATION_ITEMS: BankItem[] = [
  {
    id: "ap-xue-1",
    type: "application",
    refs: [1, 31],
    concepts: ["xue"],
    difficulty: 1,
    cognitive: "apply",
    purpose: "學而時習之落到複習方法上",
    stem: "數學課聽懂了，回家不做題就睡了。第二天考試全不會。依「學而時習之」，問題出在哪？",
    context: "月考前一週，你每天認真聽課，筆記工整，但從不動手做題。",
    options: [
      { id: "a", text: "缺了「習」：沒有動手演練過" },
      { id: "b", text: "缺了「時」：應該把複習時間排得更密集一些", why: "「時」是在適當的時候，不是頻率越高越好；此處缺的是動手這一環。" },
      { id: "c", text: "缺了「思」：應該多想想這些公式的來歷", why: "思當然要，但這裡的斷點很具體——沒有實習演練，思也沒有著力點。" },
      { id: "d", text: "沒有問題：聽懂了就是學會了，只是考試失常", why: "這正是本章要糾正的錯覺：聽過與能用是兩件事。" },
    ],
    answerId: "a",
    explanation: "「習」是實習演練。孔子那時學的是禮樂射御，非練不可；今天學的是解題，同樣非練不可——這一點兩千年沒變。",
  },
  {
    id: "ap-xue-2",
    type: "application",
    refs: [451, 31],
    concepts: ["xue"],
    difficulty: 2,
    cognitive: "apply",
    purpose: "空想與求證的分工",
    stem: "你要寫一篇關於「短視頻是否影響專注力」的文章，已經想了三個晚上，越想越亂。依 15.31，下一步該？",
    options: [
      { id: "a", text: "停止空想，去找材料與不同立場來讀" },
      { id: "b", text: "再想一晚，把思路理清楚再動筆", why: "孔子親身試過「終日不食，終夜不寢，以思」，結論是「無益，不如學也」。" },
      { id: "c", text: "先寫出來再說，寫著寫著就通了", why: "沒有材料支撐，寫出來的仍是同一堆空想。" },
      { id: "d", text: "換一個自己更有把握的題目", why: "迴避不能解決方法問題，下一題同樣會卡在這裡。" },
    ],
    answerId: "a",
    explanation: "思與學各有分工：思負責提問和整合，學負責提供可檢驗的材料。卡住的時候，通常缺的是後者。",
  },
  {
    id: "ap-xue-3",
    type: "application",
    refs: [450, 123],
    concepts: ["xue", "junzi"],
    difficulty: 2,
    cognitive: "apply",
    purpose: "「過而不改」與「不貳過」",
    stem: "同一類錯題你已經錯了三次，每次都訂正、每次都再錯。依顏回「不貳過」的標準，你缺的是？",
    options: [
      { id: "a", text: "只改了答案，沒找出錯的根源" },
      { id: "b", text: "訂正得不夠工整，應該重抄一遍題目", why: "形式上的訂正正是問題所在：抄得再工整，根源沒動，第四次還會錯。" },
      { id: "c", text: "練習量不夠，應該多做同類題", why: "量能掩蓋問題但不解決它；沒找到根源，多做只是重複同一個錯。" },
      { id: "d", text: "這很正常，錯三次還算少", why: "15.30 說「過而不改，是謂過矣」——重複而不改，才是真正的過。" },
    ],
    answerId: "a",
    explanation: "孔子稱顏回好學，舉的證據是「不遷怒，不貳過」——不是學得多快，是同一個坑不掉第二次。這是可操作的好學定義。",
  },
  {
    id: "ap-shu-1",
    type: "application",
    refs: [444, 324],
    concepts: ["shu"],
    difficulty: 1,
    cognitive: "apply",
    purpose: "恕的底線用法",
    stem: "班群裡有人轉發同學的糗照，你也覺得好笑。依「己所不欲，勿施於人」，該怎麼做？",
    options: [
      { id: "a", text: "先想換作自己如何，然後不轉不起鬨" },
      { id: "b", text: "只要不是自己發的，跟著笑一下沒關係", why: "起鬨也是「施」的一部分；恕看的是你參與造成了什麼，不是誰先動手。" },
      { id: "c", text: "立刻在群裡點名批評轉發的人", why: "恕是自己不加於人，不是取得指責他人的資格；當眾點名往往正是你自己不願承受的。" },
      { id: "d", text: "私下把照片保存下來，但不轉發", why: "不轉發是對的，保存卻仍在延續這件事。" },
    ],
    answerId: "a",
    explanation: "子貢問哪一句可以終身奉行，孔子給的就是這句。它的好處是隨時可用：不必先成為好人，只要肯做一次換位。",
  },
  {
    id: "ap-shu-2",
    type: "application",
    refs: [150],
    concepts: ["shu", "ren"],
    difficulty: 2,
    cognitive: "apply",
    purpose: "「己欲立而立人」的積極面",
    stem: "小組作業中你進度最快。依「己欲立而立人，己欲達而達人」，最合的做法是？",
    options: [
      { id: "a", text: "把自己弄懂的方法講給卡住的組員" },
      { id: "b", text: "把別人的部分也一併做完，最有效率", why: "代做讓別人「立」不起來；立人是使他能自立，不是替他站著。" },
      { id: "c", text: "先確保自己的部分完美，其餘不管", why: "這只做到「己欲立」，缺了推及他人的那一半。" },
      { id: "d", text: "放慢速度配合最慢的人，避免顯得突出", why: "遷就不是立人；孔子沒有要人壓低自己。" },
    ],
    answerId: "a",
    explanation: "「能近取譬」——從自己想要的推到別人身上。想站穩，就幫人站穩；想通達，就幫人通達。代勞和遷就都不在其中。",
  },
  {
    id: "ap-xin-1",
    type: "application",
    refs: [13, 38],
    concepts: ["xin"],
    difficulty: 2,
    cognitive: "apply",
    purpose: "先問該不該諾",
    stem: "朋友要你答應：考試時把答案傳給他，並說「你答應了就不能反悔」。依「信近於義，言可復也」，該怎麼判斷？",
    options: [
      { id: "a", text: "此諾不合義，許了也不該以信為由做" },
      { id: "b", text: "既然答應了就要做到，守信最重要", why: "有子正是要防這一步：把守信抽離義來談，就成了為錯事背書。" },
      { id: "c", text: "答應但故意傳錯答案，兩邊都不得罪", why: "這是既失信又失直，兩頭都丟。" },
      { id: "d", text: "先答應下來，到時候再看情況", why: "明知不該做而先許諾，本身已違背「言可復」的前提。" },
    ],
    answerId: "a",
    explanation: "「信近於義」是承諾的入口條件。守信之所以是德，因為它守的是該守的事；離開這個前提，守信只是把錯誤執行到底。",
  },
  {
    id: "ap-xin-2",
    type: "application",
    refs: [329],
    concepts: ["xin", "zheng"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "民信不立的現代對照",
    stem: "學生會承諾的事屢屢做不到，但活動辦得熱鬧、經費也充足。依 12.7 的排序，最該優先修補的是？",
    options: [
      { id: "a", text: "承諾與兌現之間的落差" },
      { id: "b", text: "先把活動辦得更精彩，用成績贏回人心", why: "「足兵」「足食」都可以去，「民信」不可去；用政績替代信任，正是本章排除的次序。" },
      { id: "c", text: "增加經費，讓每個承諾都有資源落實", why: "資源有助兌現，但問題出在承諾的方式，不是預算額度。" },
      { id: "d", text: "減少對外承諾，避免被追究", why: "少說可以少錯，卻換不回已經失去的信任。" },
    ],
    answerId: "a",
    explanation: "孔子把「民信」排在糧食之上，理由是「民無信不立」——沒有信任，這個組織本身就不成立。這條排序在任何團體裡都適用。",
  },
  {
    id: "ap-yili-1",
    type: "application",
    refs: [71, 82],
    concepts: ["yili"],
    difficulty: 2,
    cognitive: "apply",
    purpose: "先問該不該得",
    stem: "競賽名額有一個空缺，你發現可以透過家人的關係拿到。依「不以其道得之，不處也」，該怎麼判斷？",
    options: [
      { id: "a", text: "先問是否循該有的規則而得，否則不取" },
      { id: "b", text: "機會難得，先拿到再用實力證明自己", why: "「不以其道得之」判的正是取得的方式；事後的表現不能追認取得的正當性。" },
      { id: "c", text: "君子應當淡泊，這種名額本來就不該想要", why: "孔子承認「富與貴，是人之所欲也」，想要不是問題，取法才是。" },
      { id: "d", text: "只要沒有明文規定禁止，就可以接受", why: "「其道」是該有的道理，不等於規則有沒有寫到。" },
    ],
    answerId: "a",
    explanation: "義利之辨不是要人清心寡欲，是要人在想要的東西面前先問一句「這是不是我該得的」。想要與該得，是兩個獨立的問題。",
  },
  {
    id: "ap-yili-2",
    type: "application",
    refs: [166, 162],
    concepts: ["yili"],
    difficulty: 2,
    cognitive: "evaluate",
    purpose: "分辨可求與不可求",
    stem: "一份兼職薪水很高，但要你在宣傳裡說一些自己並不相信的話。依 7.12 與 7.16，孔子的態度會是？",
    options: [
      { id: "a", text: "財富可求，但以假話換取則不取" },
      { id: "b", text: "君子不該打工賺錢，應專心讀書", why: "他說富若可求，「雖執鞭之士，吾亦為之」——並不排斥謀生。" },
      { id: "c", text: "薪水高就值得，說幾句宣傳話不算什麼", why: "判準在「義」不在數額；本章正是就這種交換立論。" },
      { id: "d", text: "先做著，攢夠錢再去做真正想做的事", why: "把手段與目的分離，正是「不以其道得之」所排除的。" },
    ],
    answerId: "a",
    explanation: "孔子對財富的態度很清楚：不排斥，但設條件。「如不可求，從吾所好」——求不到就做自己認為對的事，並不覺得虧。",
  },
  {
    id: "ap-junzi-1",
    type: "application",
    refs: [441, 439],
    concepts: ["junzi"],
    difficulty: 2,
    cognitive: "apply",
    purpose: "求諸己的實際操作",
    stem: "小組報告拿了低分，你認為是組員拖累。依「君子求諸己，小人求諸人」，該先做什麼？",
    options: [
      { id: "a", text: "先找出自己在分工與把關上可改之處" },
      { id: "b", text: "把問題明確指出來，讓該負責的人負責", why: "釐清責任不是不能做，但「求諸己」要求先從自己這一邊找可改之處，次序不能倒。" },
      { id: "c", text: "下次不跟這些人一組", why: "換人迴避了問題，你在協作上的短板會原樣帶到下一組。" },
      { id: "d", text: "接受結果，不必檢討", why: "求諸己是主動檢討，不是消極承受。" },
    ],
    answerId: "a",
    explanation: "求諸己不是攬過，是把注意力放在自己能改變的部分。15.19「君子病無能焉，不病人之不己知也」是同一個方向。",
  },
  {
    id: "ap-junzi-2",
    type: "application",
    refs: [369, 442],
    concepts: ["junzi", "zhongyong"],
    difficulty: 3,
    cognitive: "apply",
    purpose: "和而不同的協作用法",
    stem: "討論方案時，你不贊成多數人的意見。依「君子和而不同」「矜而不爭，群而不黨」，最合的做法是？",
    options: [
      { id: "a", text: "說清異見並給理由，但不結黨不破局" },
      { id: "b", text: "為了團隊和諧，把不同意見收起來", why: "那是「同」不是「和」——抹掉差異換來的一致，正是孔子歸給小人的那一種。" },
      { id: "c", text: "私下拉幾個人一起反對，形成聲勢", why: "「群而不黨」明確排除結黨；靠人數而非理由取勝，與和無關。" },
      { id: "d", text: "堅持到底，不接受任何折衷", why: "「不同」是保留差異，不是拒絕協調；和的前提是仍要一起把事做成。" },
    ],
    answerId: "a",
    explanation: "和是不同成分配得起來。說出差異是「不同」，仍把事做成是「和」——兩者要同時成立，缺哪一半都不是君子之道。",
  },
  {
    id: "ap-zhiren-1",
    type: "application",
    refs: [26, 448],
    concepts: ["zhiren"],
    difficulty: 2,
    cognitive: "apply",
    purpose: "眾好眾惡都要察",
    stem: "網上一面倒地罵某個人，你並不了解事情經過。依「眾惡之，必察焉；眾好之，必察焉」，該怎麼做？",
    options: [
      { id: "a", text: "不因人多而從，先看事情本身的證據" },
      { id: "b", text: "多數人的判斷通常不會錯，可以跟從", why: "本章要防的正是這個直覺；13.24「鄉人皆好之，未可也」是同一意思。" },
      { id: "c", text: "一面倒的事多半有內情，應該站到少數那一邊", why: "反過來盲從少數，同樣是省掉了「察」這一步。" },
      { id: "d", text: "與己無關，不必表態也不必了解", why: "不表態可以，但本章要求的是查證，不是迴避。" },
    ],
    answerId: "a",
    explanation: "「必察焉」是一道獨立工序：無論眾人說好說壞，你都得自己看一遍。這句話在信息過載的今天，比在孔子的時代更難做到。",
  },
  {
    id: "ap-zhiren-2",
    type: "application",
    refs: [26, 443],
    concepts: ["zhiren"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "視所以觀所由察所安的實用",
    stem: "有人主動幫你很多忙。依「視其所以，觀其所由，察其所安」，判斷他為人的次序是？",
    options: [
      { id: "a", text: "先看所做，再看所由，最後看所安" },
      { id: "b", text: "先看他的動機，動機純正就可信", why: "動機最難直接看到，所以孔子從可觀察的行為起步，把「所安」放在最後。" },
      { id: "c", text: "先問清楚他想要什麼回報", why: "直接盤問既不禮貌也未必得到真話；三層觀察是不必發問的方法。" },
      { id: "d", text: "看他對別人是不是也一樣，就夠了", why: "這是有用的一面，但只覆蓋了「所由」，缺了另外兩層。" },
    ],
    answerId: "a",
    explanation: "由外而內，一層比一層難偽裝。最後一層「所安」看的是他做這件事時自不自在——勉強和樂意，行為一樣，神情不一樣。",
  },
  {
    id: "ap-xiao-1",
    type: "application",
    refs: [84, 23],
    concepts: ["xiao"],
    difficulty: 2,
    cognitive: "apply",
    purpose: "孝與異議並存",
    stem: "父母替你決定了選科，你並不認同。依「事父母幾諫，見志不從，又敬不違」，該怎麼處理？",
    options: [
      { id: "a", text: "平心說明而不頂撞，不從仍敬不怨" },
      { id: "b", text: "父母的決定就是對的，照辦不必多說", why: "「幾諫」明說要勸；一句不說，反而不合本章。" },
      { id: "c", text: "堅持自己的選擇，先斬後奏", why: "「又敬不違」排除了對抗式的處理。" },
      { id: "d", text: "表面答應，私下按自己的來", why: "這是欺瞞；孝與信在此同時被放棄。" },
    ],
    answerId: "a",
    explanation: "《論語》裡的孝從不要求放棄判斷，只要求表達判斷時保持敬。勸與不逆，是同一件事的兩面。",
  },
  {
    id: "ap-xiao-2",
    type: "application",
    refs: [24, 87],
    concepts: ["xiao"],
    difficulty: 1,
    cognitive: "apply",
    purpose: "色難的日常對照",
    stem: "你每月給家裡轉生活費，但每次通話都不耐煩。依「色難」，這算不算盡到孝？",
    options: [
      { id: "a", text: "供養做到了，難的那一層還沒做到" },
      { id: "b", text: "算：能負擔家用已經是最實在的孝", why: "2.7 正是針對這一點：能養而不敬，「何以別乎」。" },
      { id: "c", text: "不算：應該辭掉工作回家陪伴", why: "本章不要求放棄本業，只要求把態度補上。" },
      { id: "d", text: "算：態度是小事，心意到了就好", why: "「色難」說的正是態度並非小事，它是心意唯一看得見的形式。" },
    ],
    answerId: "a",
    explanation: "孔子把最難的一項點在臉色上，因為它裝不久。錢可以按月轉，好臉色得每次現做——這正是它難的原因。",
  },
  {
    id: "ap-ren-1",
    type: "application",
    refs: [69, 70],
    concepts: ["ren"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "仁者能好人能惡人",
    stem: "有同學長期排擠一個人。你想做個「與人為善」的人，因此不表態。依「唯仁者能好人，能惡人」，這樣對嗎？",
    options: [
      { id: "a", text: "不對：仁有明確是非，該惡要能惡" },
      { id: "b", text: "對：仁者應當包容所有人，包括排擠者", why: "包容一切正是把仁讀成無原則的和氣，本章直接否定了這一點。" },
      { id: "c", text: "對：不介入別人的事才是分寸", why: "分寸不等於在明顯的傷害前退場；17.13「鄉愿，德之賊也」批的就是這種無害的中立。" },
      { id: "d", text: "不對：應當立刻公開指責排擠者", why: "方向對但過了頭；孔子要的是判斷力，不是姿態。" },
    ],
    answerId: "a",
    explanation: "仁者有好惡，而且只有仁者的好惡才可靠——因為那不是出於私利。取消好惡的「善良」，孔子給它取了個名字叫鄉愿。",
  },
  {
    id: "ap-ren-2",
    type: "application",
    refs: [180, 257],
    concepts: ["ren"],
    difficulty: 2,
    cognitive: "apply",
    purpose: "仁在當下可及",
    stem: "你覺得「行仁」是很大的事，自己還不夠格。依「我欲仁，斯仁至矣」，該怎麼想？",
    options: [
      { id: "a", text: "仁不在遠處，是此刻可做的選擇" },
      { id: "b", text: "要先修養多年，德行到位了才談得上行仁", why: "本章正是反對把仁推遠：「仁遠乎哉？」" },
      { id: "c", text: "行仁要有能力和地位，等以後再說", why: "6.30「能近取譬」明說從身邊推起，不待條件。" },
      { id: "d", text: "只要心裡有善意就已經是仁", why: "起念是入口，仍要落到行事上；孔子談仁多在具體做法。" },
    ],
    answerId: "a",
    explanation: "馬廄失火，孔子問「傷人乎」，不問馬。仁在那一瞬間就是這麼具體：先問哪一件事——它從來不是一項需要準備的大工程。",
  },
  {
    id: "ap-li-1",
    type: "application",
    refs: [44, 487],
    concepts: ["li"],
    difficulty: 2,
    cognitive: "evaluate",
    purpose: "禮的本在敬不在排場",
    stem: "班級要辦畢業典禮，同學爭論該把預算放在場地布置還是流程與致辭的用心上。依 3.4 與 17.11，孔子會傾向？",
    options: [
      { id: "a", text: "寧可從簡而心意到位，與其奢也寧儉" },
      { id: "b", text: "隆重才顯得鄭重，應當把場面辦足", why: "3.4 正是在奢與儉之間選了儉；17.11 更直接否定以器物代禮。" },
      { id: "c", text: "禮是形式，兩邊都不重要", why: "孔子極重禮，只是把重心放在敬上，不是取消形式。" },
      { id: "d", text: "按慣例辦即可，不必討論", why: "林放問「禮之本」被讚為「大哉問」，可見這個討論本身正是該做的。" },
    ],
    answerId: "a",
    explanation: "孔子不反對形式，他反對形式脫離了它要承載的敬意。判準很簡單：這樣做，是為了誰看。",
  },
  {
    id: "ap-li-2",
    type: "application",
    refs: [190],
    concepts: ["li", "junzi"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "四種德行都需要禮來調節",
    stem: "「恭而無禮則勞，慎而無禮則葸，勇而無禮則亂，直而無禮則絞」——你是個直率的人，常因說話太衝得罪人。這章給你的提示是？",
    options: [
      { id: "a", text: "直是好的，要補的是禮而非改掉直" },
      { id: "b", text: "應該改掉直率，學會圓滑", why: "本章沒有否定直，只指出它需要調節；改成圓滑就落到另一頭去了。" },
      { id: "c", text: "說話衝是性格問題，改不了", why: "「無禮」是可補的條件，這正是本章給出的著力點。" },
      { id: "d", text: "只要出發點是好的，說話方式不重要", why: "「絞」正是說出發點好卻傷人的那種結果。" },
    ],
    answerId: "a",
    explanation: "這一章的結構很有用：恭、慎、勇、直四項都是優點，缺了禮就各自變質。禮在這裡是分寸，不是規矩本身。",
  },
  {
    id: "ap-zheng-1",
    type: "application",
    refs: [352, 339],
    concepts: ["zheng"],
    difficulty: 2,
    cognitive: "apply",
    purpose: "其身正不令而行",
    stem: "你當班長，要求大家守時，但自己常遲到。依「其身正，不令而行；其身不正，雖令不從」，關鍵在？",
    options: [
      { id: "a", text: "先做到自己所要求的，令才有效力" },
      { id: "b", text: "加重處罰，讓規則有威懾力", why: "2.3 說得明白：靠罰只換來「民免而無恥」，換不來自願。" },
      { id: "c", text: "把規則寫得更詳細一些", why: "問題不在規則不清楚，在發令的人自己不做。" },
      { id: "d", text: "找幾個人帶頭配合，形成風氣", why: "風氣有用，但源頭仍是 12.19「君子之德風」——帶頭的是你。" },
    ],
    answerId: "a",
    explanation: "「政者，正也」——孔子把治理直接定義成端正自己。這不是道德勸說，是效力問題：不正的人發的令，別人只是應付。",
  },
  {
    id: "ap-zheng-2",
    type: "application",
    refs: [19],
    concepts: ["zheng", "li"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "罰則與羞恥心的差別",
    stem: "宿舍衛生一直不好，管理員改成扣分罰款後乾淨了。依 2.3，這個結果算不算成功？",
    options: [
      { id: "a", text: "只到民免而無恥：撤了罰就會回去" },
      { id: "b", text: "算成功：結果乾淨了，方法就是好的", why: "孔子看的是行為背後有沒有立起羞恥心；只看結果，正是本章要區分的那一層。" },
      { id: "c", text: "不算：孔子反對一切懲罰措施", why: "他沒說刑政不能用，只說它不夠。" },
      { id: "d", text: "算：罰款也是一種禮的落實", why: "禮的作用是養恥，罰的作用是止行，兩者機制不同。" },
    ],
    answerId: "a",
    explanation: "這一章給了一把很好用的尺：問「撤掉懲罰後會怎樣」。答案若是「打回原形」，那就只做到了「免」，還沒到「格」。",
  },
  {
    id: "ap-zhongyong-1",
    type: "application",
    refs: [286, 149],
    concepts: ["zhongyong"],
    difficulty: 2,
    cognitive: "apply",
    purpose: "過與不及的自我診斷",
    stem: "準備演講，你反覆修改到最後一刻，稿子越改越亂；同伴則完全不準備。依「過猶不及」，怎麼看這兩種狀態？",
    options: [
      { id: "a", text: "兩者都不合格，都沒對準所需的量" },
      { id: "b", text: "你比同伴好，因為努力總是對的", why: "子貢正是這樣問「然則師愈與」，孔子答「過猶不及」。" },
      { id: "c", text: "取兩人的平均就對了", why: "恰好由事情本身決定，不是兩個人的中點。" },
      { id: "d", text: "看結果，誰講得好誰就對", why: "結果有運氣成分；本章給的是事前判斷的標準。" },
    ],
    answerId: "a",
    explanation: "「過猶不及」最有用的地方是它取消了「越努力越好」的預設。要問的不是「我夠不夠拚」，是「這件事需要多少」。",
  },
  {
    id: "ap-chushi-1",
    type: "application",
    refs: [201, 202],
    concepts: ["chushi"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "用能否守道而非能否自保來判斷進退",
    stem: "社團風氣不正，你是幹部。依「邦有道則見，無道則隱」與「邦有道，貧且賤焉，恥也」，該怎麼選？",
    options: [
      { id: "a", text: "先判斷還能否守住該守的，再定去留" },
      { id: "b", text: "立刻退出，避免沾上是非", why: "「隱」的前提是無道到守不住；環境一有問題就退，正是孔子說的另一種恥。" },
      { id: "c", text: "留下來但不作為，安全第一", why: "留而不為既不是見也不是隱，兩頭落空。" },
      { id: "d", text: "留下來並用同樣的方式往上爬，先站穩再說", why: "以同流換取位置，正是「不以其道得之」。" },
    ],
    answerId: "a",
    explanation: "出處問題沒有現成答案，孔子給的是判準而不是選項：看你在那裡還能不能是你自己。這也是他一生反覆在做的判斷。",
  },
  {
    id: "ap-jiaoxue-1",
    type: "application",
    refs: [158, 292],
    concepts: ["jiaoxue"],
    difficulty: 2,
    cognitive: "apply",
    purpose: "掌握啟發的火候：等到憤悱之際再點",
    stem: "你在輔導學弟，他盯著題目皺眉但還沒開口。依「不憤不啟，不悱不發」，此刻該？",
    options: [
      { id: "a", text: "再等一會，待他自己卡住再點一句" },
      { id: "b", text: "立刻把完整解法講一遍，節省時間", why: "講早了，那口氣就散了；他接收到的是答案，不是思路。" },
      { id: "c", text: "讓他自己想，完全不要介入", why: "「啟」與「發」仍要做，只是要等到時候——完全不介入是另一個極端。" },
      { id: "d", text: "先問他基礎概念會不會", why: "可以問，但此刻他正在「憤」的狀態，打斷反而浪費了最好的時機。" },
    ],
    answerId: "a",
    explanation: "教學的火候：學生想通未通的那一刻，是最容易接住提示的時候。早一步是灌輸，晚一步是錯過。",
  },
  {
    id: "ap-jiaoxue-2",
    type: "application",
    refs: [292],
    concepts: ["jiaoxue", "zhiren"],
    difficulty: 3,
    cognitive: "analyze",
    purpose: "同問異答的道理",
    stem: "兩個朋友都問你「該不該馬上去做這件事」。一個一向衝動，一個一向猶豫。依 11.22，你的回答應該？",
    options: [
      { id: "a", text: "對衝動的說緩一緩，對猶豫的說去做" },
      { id: "b", text: "給兩人同樣的答案，才算公平", why: "公孫赤也覺得奇怪，孔子的回答是「求也退，故進之；由也兼人，故退之」。" },
      { id: "c", text: "都建議再想想，穩妥為上", why: "對本來就猶豫的人說再想想，是加重他的毛病。" },
      { id: "d", text: "都建議立刻去做，行動最重要", why: "對本來就衝動的人說立刻去做，同樣是加重毛病。" },
    ],
    answerId: "a",
    explanation: "同一句話對不同的人有不同的作用。孔子答問從不追求一致，他追求的是對這個人此刻有用——這是因材施教最清楚的一次示範。",
  },
  {
    id: "ap-wenzhi-1",
    type: "application",
    refs: [138, 330],
    concepts: ["wenzhi"],
    difficulty: 2,
    cognitive: "apply",
    purpose: "內容與表達都要",
    stem: "你的報告內容紮實但排版凌亂、講述含糊；同學的報告漂亮但空洞。依「文質彬彬」，怎麼評價？",
    options: [
      { id: "a", text: "兩份各偏一邊：一質而野，一文而史" },
      { id: "b", text: "你的更好，因為內容才是關鍵", why: "6.18 兩邊都判為失；子貢更說「文猶質也，質猶文也」，不分主次。" },
      { id: "c", text: "他的更好，因為呈現決定第一印象", why: "同理，文勝質則史，一樣不合格。" },
      { id: "d", text: "各有所長，不必比較", why: "本章給的正是一個統一標準，不是各自為政。" },
    ],
    answerId: "a",
    explanation: "「文猶質也，質猶文也。虎豹之鞟猶犬羊之鞟」——去掉花紋的皮革就分不出虎豹和犬羊。表達不是裝飾，它是內容得以被辨認的方式。",
  },
  {
    id: "ap-shijiao-1",
    type: "application",
    refs: [485, 351],
    concepts: ["shijiao"],
    difficulty: 2,
    cognitive: "apply",
    purpose: "讀文學的功能性檢驗",
    stem: "你背了很多詩詞，但寫作文時用不上、與人談話也引不出來。依 13.5 與 17.9，問題在？",
    options: [
      { id: "a", text: "背誦未轉成能力，用不出來就不算學到" },
      { id: "b", text: "背得還不夠多，應該再多背一些", why: "13.5 說「雖多，亦奚以為」——數量正是被排除的解釋。" },
      { id: "c", text: "詩詞本來就沒有實用價值", why: "17.9 列了四項功能，孔子從不認為它無用。" },
      { id: "d", text: "應該改讀應用文，不必讀詩", why: "問題在讀法不在讀物。" },
    ],
    answerId: "a",
    explanation: "「授之以政，不達；使於四方，不能專對」——孔子給《詩》設了兩個很硬的驗收場景。學了能不能用，是他一貫的檢驗方式。",
  },
  {
    id: "ap-tianming-1",
    type: "application",
    refs: [411, 414],
    concepts: ["tianming"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "盡人事與知天命並存",
    stem: "你為一件事準備了很久，結果仍不如意。依「不怨天，不尤人」與「知其不可而為之」，該怎麼安放這件事？",
    options: [
      { id: "a", text: "該做的已做，結果有不由自己的部分" },
      { id: "b", text: "既然結果不由自己，以後不必那麼認真", why: "知命是安於結果，不是取消行動；孔子明知不可仍然為之。" },
      { id: "c", text: "一定是自己還不夠努力，要更拚", why: "把一切歸因於自己，正是「尤人」的反面版本，同樣沒有承認限度。" },
      { id: "d", text: "應該歸咎於環境和運氣", why: "「不怨天，不尤人」直接排除了這一條。" },
    ],
    answerId: "a",
    explanation: "知命最實際的用處，是把「我能決定的」和「我不能決定的」分開。前者全力以赴，後者不拿來折磨自己——這才是「不怨不尤」的內容。",
  },
  {
    id: "ap-zhengming-1",
    type: "application",
    refs: [349, 333],
    concepts: ["zhengming"],
    difficulty: 3,
    cognitive: "apply",
    purpose: "名實相符的組織用法",
    stem: "社團裡有人掛著「副社長」的名，實際什麼都不做；真正做事的人沒有名分。依「必也正名乎」，該先處理什麼？",
    options: [
      { id: "a", text: "先讓名分與實際承擔對得上" },
      { id: "b", text: "名分是虛的，先把事做完再說", why: "孔子的判斷正相反：「名不正則言不順，言不順則事不成」，名是事的前提。" },
      { id: "c", text: "取消所有頭銜，大家平等做事", why: "取消名分不等於正名；沒有名分，責任同樣無處落。" },
      { id: "d", text: "讓掛名的人補做一些事，把名分保住", why: "這是遷就名分而扭曲實際，方向反了。" },
    ],
    answerId: "a",
    explanation: "子路也覺得孔子迂，正名哪有那麼要緊。但孔子把因果鏈說得很細：名—言—事—禮樂—刑罰—民無所措手足。名分是這條鏈的起點。",
  },
  {
    id: "ap-zhiren-3",
    type: "application",
    refs: [466],
    concepts: ["zhiren", "xin"],
    difficulty: 1,
    cognitive: "apply",
    purpose: "益者三友的實際判斷",
    stem: "依「友直，友諒，友多聞，益矣；友便辟，友善柔，友便佞，損矣」，下面哪種朋友屬於「益者」？",
    options: [
      { id: "a", text: "會直說你哪裡錯了，即使你不愛聽" },
      { id: "b", text: "無論你做什麼都說好，讓你心情愉快", why: "這正是「善柔」——一味順從討好，是損者三友之一。" },
      { id: "c", text: "很會說話，能把任何事講得動聽", why: "「便佞」指巧言而無實，同屬損者。" },
      { id: "d", text: "很懂得看場合說話，從不讓人尷尬", why: "「便辟」指習於逢迎、避開實話，也在損者之列。" },
    ],
    answerId: "a",
    explanation: "三益三損正好兩兩相對：直對便辟，諒（誠信）對善柔，多聞對便佞。共同點是——益友讓你接觸到真實，損友讓你舒服。",
  },
  {
    id: "ap-xue-4",
    type: "application",
    refs: [172, 83],
    concepts: ["xue", "zhiren"],
    difficulty: 1,
    cognitive: "apply",
    purpose: "見不賢而內自省",
    stem: "你很討厭某個同學愛炫耀。依「見賢思齊焉，見不賢而內自省也」，這件事的正確用法是？",
    options: [
      { id: "a", text: "拿他當鏡子，查自己有無同類毛病" },
      { id: "b", text: "確認自己比他強，因此可以放心", why: "「內自省」的方向是向內查自己，不是向外做比較。" },
      { id: "c", text: "提醒他改掉這個毛病", why: "提醒未必不對，但本章給的動作是自省，不是規勸他人。" },
      { id: "d", text: "遠離他，避免受影響", why: "7.22 說「其不善者而改之」——不善者也有用處，不必只是躲開。" },
    ],
    answerId: "a",
    explanation: "討厭一個人的某種毛病，通常說明你對這毛病很敏感。孔子把這份敏感掉轉方向：先照自己一遍，比評論別人有用得多。",
  },
  {
    id: "ap-ren-3",
    type: "application",
    refs: [338],
    concepts: ["ren", "junzi"],
    difficulty: 2,
    cognitive: "apply",
    purpose: "成人之美的具體做法",
    stem: "同學要參加一個比賽，你也想參加但機會只有一個，而他準備得比你充分。依「君子成人之美，不成人之惡」，該？",
    options: [
      { id: "a", text: "支持他去，並把知道的資訊也給他" },
      { id: "b", text: "退讓並從此不再爭取這類機會", why: "成人之美不要求放棄自己；下次條件不同，該爭仍可爭。" },
      { id: "c", text: "照常競爭，各憑本事，不必幫忙", why: "競爭本身沒錯，但本章講的正是在競爭中仍不做拆台的事，且能主動成全。" },
      { id: "d", text: "幫他，但期待他下次讓給自己", why: "附帶條件的成全是交易，不是成人之美。" },
    ],
    answerId: "a",
    explanation: "「小人反是」——反過來就是見不得別人好、順手推一把使其壞。這一章的分界線很清楚：你希望這件好事發生，還是希望它發生在你身上。",
  },
  {
    id: "ap-junzi-3",
    type: "application",
    refs: [403, 88, 90],
    concepts: ["junzi", "xin"],
    difficulty: 2,
    cognitive: "apply",
    purpose: "把說話的門檻提高，先行而後言",
    stem: "你在群裡宣布了一個很大的計畫，還沒開始做就收到很多稱讚。依「君子恥其言而過其行」與「先行其言而後從之」，此刻該？",
    options: [
      { id: "a", text: "停止再宣告，先把第一步做出來" },
      { id: "b", text: "趁勢多說一些，用外界期待推動自己", why: "2.13 給子貢的答案正相反：先做，做了再說。" },
      { id: "c", text: "把計畫縮小，以免說得太滿", why: "縮小規模不解決次序問題；問題在先說後做。" },
      { id: "d", text: "沒關係，說出來也是一種承諾", why: "4.22「古者言之不出，恥躬之不逮也」——怕的正是說了做不到。" },
    ],
    answerId: "a",
    explanation: "孔子講言行的話特別多，方向一致：把說話的門檻提高，把行動的門檻降低。稱讚來得太早，往往就是行動停下的地方。",
  },
  {
    id: "ap-li-3",
    type: "application",
    refs: [55, 261],
    concepts: ["li", "xue"],
    difficulty: 2,
    cognitive: "apply",
    purpose: "每事問不是無知",
    stem: "新加入一個團隊，很多流程你不清楚。有人說「問這麼多顯得不專業」。依「子入太廟，每事問」，該怎麼做？",
    options: [
      { id: "a", text: "該問就問，慎重確認本身就是敬" },
      { id: "b", text: "先自己摸索，實在不行再問，以免被看輕", why: "怕被看輕正是那位「鄹人之子」譏諷者的邏輯，孔子沒有接受。" },
      { id: "c", text: "只問重要的，小事自己猜", why: "「每事問」的重點正在不憑猜測；猜錯的往往是小事。" },
      { id: "d", text: "問了顯得不專業，應該裝作知道", why: "5.15 稱許孔文子「不恥下問」，裝懂與此完全相反。" },
    ],
    answerId: "a",
    explanation: "被人譏笑「這也算知禮嗎」，孔子答「是禮也」——在該慎重的場合逐項確認，本身就是敬。問，不是無知的證明，是慎重的形式。",
  },
  {
    id: "ap-zhongyong-2",
    type: "application",
    refs: [489, 370],
    concepts: ["zhongyong", "zhiren"],
    difficulty: 3,
    cognitive: "evaluate",
    purpose: "辨認鄉愿：一面倒的好評本身就可疑",
    stem: "有個人人人都說他好，從不得罪任何人，任何爭議中都不表態。依「鄉愿，德之賊也」與 13.24，該如何評價？",
    options: [
      { id: "a", text: "人人都說好本身就值得警惕" },
      { id: "b", text: "人緣好說明德行好，值得學習", why: "13.24 明說「鄉人皆好之，未可也」，一面倒的好評不是合格證明。" },
      { id: "c", text: "不表態是明哲保身，無可厚非", why: "孔子把這種無害的中立稱為「德之賊」，因為它讓是非失去分量。" },
      { id: "d", text: "應該直接指責他虛偽", why: "本章要練的是辨識，不是攻擊；且是否虛偽仍需 2.10 三層觀察。" },
    ],
    answerId: "a",
    explanation: "「德之賊」用詞極重。鄉愿的問題不是做壞事，是它讓「好人」這個標準失效——當誰都能得到好評，好評就不再說明任何事。",
  },
];
